import React, { useEffect, useState } from 'react';
import { RHP } from 'providers';
import { ContextAuth as CA } from 'contexts';
import { useForm } from 'components/form';
import * as Form from 'components/form';
import * as Page from 'components/page';
import * as Alert from 'components/alert';
import { useEffectOnce } from 'react-use';
import { useConversionOptions, useApiLocation } from 'hooks';

export function CorporateStep2(props) {
    const { vars, updateSteps, putCompanyDetails } = props.useRankingCorporate;
    const { ranking, companyDetails } = vars;
    const { state } = React.useContext(CA);
    const [countries, setCountries] = useState([]);
    const [authorizedToEdit, setAuthorizedToEdit] = useState();
    const [requireSupportEdit, setRequireSupportEdit] = useState(false);
    const [isCompanyValid] = useState(companyDetails.valid);
    const [corpHasSector] = useState(companyDetails.companySector);
    const [corpHasPocs] = useState(companyDetails.hasPocs > 0);
    const isOperational = state?.tokn?.pocs.includes('operational');
    const apiLocation = useApiLocation();
    const companyId = state.user.company.id;
    const companyName = state.user.company.name;
    const conv = useConversionOptions(['challenge', 'companySize', 'companySector', 'companyType']);
    const conversion = useConversionOptions(['corpInvestmentOrRevenues'], {
        namespace: 'currency',
    });

    useEffect(() => {
        if (corpHasPocs) {
            if (isOperational) {
                setAuthorizedToEdit(true);
            } else {
                setAuthorizedToEdit(false);
                setRequireSupportEdit(true);
            }
        } else if (!corpHasPocs && !isOperational) {
            setAuthorizedToEdit(false);
            setRequireSupportEdit(true);
        }
    }, [
        isCompanyValid,
        authorizedToEdit,
        requireSupportEdit,
        corpHasSector,
        corpHasPocs,
        isOperational,
    ]);

    useEffectOnce(() => {
        apiLocation.geo('').then(res => {
            const names = res.map(country => {
                return { name: country.name, value: country.id };
            });
            setCountries(names);
        });
    });
    // Initialize a new form handler
    const form = useForm({
        ...vars.application,
        name: companyDetails.name,
        logourl: companyDetails.logourl,
        companySector: companyDetails.companySector,
        revenuesRange: companyDetails.revenuesRange,
        companyType: companyDetails.companyType,
        size: companyDetails.size,
        taxId: companyDetails.taxId,
        country: companyDetails.country,
        multinational: companyDetails.multinational,
    });

    // Check for previous step conclusion
    if (!vars.application.steps || vars.application.steps.step1 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }

    if (!companyDetails.rankingData) {
        companyDetails.rankingData = {};
    }

    if (!companyDetails.rankingData?.[ranking.id]) {
        companyDetails.rankingData[ranking.id] = {
            revenuesRange: [],
            investmentRange: [],
            categories: [],
        };
    }

    let hasRevenuesRange =
        companyDetails.revenuesRange?.length > 0 &&
        !companyDetails.revenuesRange !== undefined &&
        !companyDetails.revenuesRange !== null &&
        !companyDetails.revenuesRange !== [] &&
        !companyDetails.revenuesRange !== '' &&
        !companyDetails.revenuesRange !== ' ';

    let hasRevenuesRangeDisplayedOption = false;
    conversion.corpInvestmentOrRevenues.forEach(i => {
        if (i.value === `range-${companyDetails.revenuesRange}`) {
            hasRevenuesRange = true;
            hasRevenuesRangeDisplayedOption = true;
        }
    });
    console.log(hasRevenuesRangeDisplayedOption, 'hasRevenuesRangeDisplayedOption');
    const fields = {
        name: {
            error: form.errors.name,
            label: props.t('ranking:CorporateStep2.companyName'),
            name: 'name',
            type: 'text',
            value: form.values.name,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.name,
        },
        logourl: {
            error: form.errors.logourl,
            label: `${props.t('ranking:CorporateStep2.logoDescr')}`,
            name: 'logourl',
            type: 'file',
            value: form.values.logourl,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.logourl,
        },
        companySector: {
            error: form.errors.companySector,
            label: `${props.t('ranking:CorporateStep2.sector')}`,
            name: 'companySector',
            type: 'select',
            options: [{ name: props.t('glossary:select'), value: '' }, ...conv.companySector],
            value: form.values.companySector,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.companySector,
        },
        taxId: {
            error: form.errors.taxId,
            label: props.t('glossary:cnpj'),
            name: 'taxId',
            type: 'generic',
            value: form.values.taxId,
            onChange: form.handleChange,
            required: false,
            readOnly: !authorizedToEdit && companyDetails.taxId,
        },
        companyType: {
            error: form.errors.companyType,
            label: `${props.t('ranking:CorporateStep2.companyType')}`,
            name: 'companyType',
            type: 'select',
            options: [{ name: props.t('glossary:select'), value: '' }, ...conv.companyType],
            value: form.values.companyType,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.companyType,
        },
        size: {
            error: form.errors.size,
            label: props.t('glossary:companySize'),
            name: 'size',
            options: [{ name: props.t('glossary:select'), value: '' }, ...conv.companySize],
            type: 'select',
            value: form.values.size,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.size,
        },
        revenuesRange: {
            error: form.errors.revenuesRange,
            label: `${props.t('glossary:revenuesRange')}`,
            name: 'revenuesRange',
            type: 'SelectRangeArray',
            options: [
                !hasRevenuesRangeDisplayedOption
                    ? {
                          name: companyDetails.revenuesRangeText,
                          value: `range-${companyDetails.revenuesRange}`,
                          disabled: !hasRevenuesRangeDisplayedOption,
                      }
                    : {
                          name: props.t('glossary:selectOption'),
                          value: '',
                      },

                ...conversion.corpInvestmentOrRevenues,
            ],
            value: form.values.revenuesRange,
            onChange: form.handleChange,
            required: true,
            readOnly: !authorizedToEdit && hasRevenuesRange,
        },
        country: {
            error: form.errors.country,
            label: `${props.t('ranking:CorporateStep2.companyLocation')}`,
            name: 'country',
            type: 'select',
            // options: countries,
            options: [{ name: props.t('glossary:select'), value: countries.id }, ...countries],
            value: form.values.country,
            // required: false,
            onChange: form.handleChange,
            readOnly: !authorizedToEdit && companyDetails.country,
        },
        multinational: {
            error: form.errors.multinational,
            label: props.t('glossary:isMultinational'),
            name: 'multinational',
            type: 'checkbox',
            required: false,
            value: form.values.multinational,
            onChange: form.handleChange,
            disabled: !authorizedToEdit && companyDetails.country,
        },
    };

    //BANNERS RELATED
    let isCompanySmall =
        fields.size?.value?.toString() === '1' || fields?.size?.value?.toString() === '2';
    let corpDontMeetRequirements =
        isCompanySmall && Number(fields.revenuesRange?.value[1]) <= 20000000;
    let notDeclaredRevenues =
        fields?.revenuesRange?.value?.length === 1 && Number(fields?.revenuesRange?.value[0]) === 0;

    let isCorp =
        Number(fields.size?.value) > 2 || Number(fields.revenuesRange?.value[0]) >= 20000000;
    let isMiddleMarket =
        isCorp &&
        !!fields?.revenuesRange?.value[1] &&
        Number(fields.revenuesRange?.value[1]) <= 200000000;

    const propsCorpDoesntMeetRequirements = {
        descr: props.t('CorporateStep2.corpDoesntMeetRequirements'),
        icons: 'far fa-exclamation-circle',
        type: 'warning',
        bgColor: '#f44336',
        color: 'white',
    };
    const propsCorpIsMiddleMarket = {
        descr: props.t('CorporateStep2.corpIsMiddleMarket'),
        icons: 'far fa-info-circle',
        bgColor: 'rgba(69, 170, 242, 0.55)',
        type: 'warning',
    };

    const propsNotDeclaredRevenues = {
        descr: props.t('CorporateStep2.notDeclaredRevenues'),
        icons: 'far fa-info-circle',
        type: 'warning',
    };
    // Handle form submitions
    const onSubmit = values => {
        const rankingPayload = {
            ...vars.application,
            revenuesRange: values.revenuesRange,
        };
        // Set the current step to true;
        if (!rankingPayload.steps) rankingPayload.steps = {};
        rankingPayload.steps.step2 = true;

        const companyPayload = {
            name: values.name,
            logourl: values.logourl,
            companySector: values.companySector,
            companyType: values.companyType,
            revenuesRange: values.revenuesRange,
            size: values.size,
            country: values.country !== 'Selecionar' ? values.country : '',
            multinational: values.country !== 'Selecionar' ? values.multinational : false,
            taxId: values.taxId,
            revenuesRangeCurrency: 'USD',
        };

        return putCompanyDetails(companyPayload)
            .then(() => {
                updateSteps({ step2: true });
                RHP.push('/ranking/latam/corporate/step3');
            })
            .catch(() => form.submited());
    };
    const evSubmit = ev => form.handleSubmit(ev, onSubmit);

    return (
        <Page.Page
            name="CorporateStep2"
            back="/ranking/latam/corporate"
            customBackText={props.t('glossary:backToRanking')}
        >
            <Page.Title>{props.t('CorporateStep2.title')}</Page.Title>
            <Page.Descr>
                {props.t('CorporateStep2.descr', { rankingYear: ranking.year })}
            </Page.Descr>

            <Form.Form name="CorporateStep2Form" onSubmit={evSubmit}>
                <Form.Field {...fields.name} />
                <Form.Field {...fields.companySector} />
                <Form.Field {...fields.companyType} />
                <Form.Field {...fields.taxId} />
                <Form.Field {...fields.size} />

                <Form.Field {...fields.revenuesRange} />
                <Form.Field {...fields.country} />

                {fields.country?.value && fields.country?.value !== 'Selecionar' && (
                    <Form.Field {...fields.multinational} />
                )}

                {corpDontMeetRequirements && (
                    <Alert.AlertRow {...propsCorpDoesntMeetRequirements} />
                )}
                {isMiddleMarket && <Alert.AlertRow {...propsCorpIsMiddleMarket} />}
                {notDeclaredRevenues && <Alert.AlertRow {...propsNotDeclaredRevenues} />}
                {requireSupportEdit && (
                    <Page.Descr>
                        {props.t('CorporateStep2.editCorporate', {
                            companyId,
                            companyName,
                        })}
                    </Page.Descr>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <Page.Buttn style={{ width: '25%' }} to="/ranking/latam/corporate/step1">
                        <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                        <span className="text" style={{ margin: '0 0 0 auto' }}>
                            {props.t('glossary:back')}
                        </span>
                    </Page.Buttn>
                    <div style={{ width: '80%' }}>
                        <Form.Submt paddingTop="10rem" {...form} text={props.t('glossary:save')} />
                    </div>
                </div>
            </Form.Form>
        </Page.Page>
    );
}
export default CorporateStep2;
